// ====================================================================
// NUMBERTON — the numbers & math zone, down the south road
// ====================================================================
// The fourth cardinal zone, and the one that finally makes the south road lead
// somewhere real: for a long time N/E/W reached themed zones while the south
// just dead-ended at the Village Green picnic. Numberton mirrors the others —
// its terrain is a plateau mask in the heightfield (centred at (0, -44)), its
// scatter props join the chunk system, and its buildings reuse the exact
// builders the village uses, passed in via ctx.
//
// What's in it: three game huts wired to math minigames through the same slug
// machinery (all Coming Soon until their embeds ship — add a slug to
// PLAYABLE_GAME_SLUGS in main.js to switch one live), a Counting House, and a
// giant Abacus that teaches a Number of the Day and gently slides its beads.
//
// Chromebook discipline: the abacus is one merged frame + a small pool of bead
// meshes; the counting garden blocks are the same cheap box-with-canvas-face
// trick Wordville uses for its letter blocks.
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { LOW } from '../../core/tier.js';
import { PALETTE, BUILDING_SCHEMES } from '../../core/palette.js';
import { addObstacle } from '../collision.js';
import { markDynamic } from '../freezeStatics.js';
import { heightAt } from '../heightfield.js';
import { makeCanvasTexture } from '../../core/texture.js';

export const NUMBERTON_CENTER = { x: 0, z: -44 };

// One kid-sized number-fact per calendar day, same for the whole class.
const NUMBERS_OF_THE_DAY = [
  ['7',   'a lucky number — count your days in a week!'],
  ['10',  'all your fingers together — a perfect ten!'],
  ['3',   'a triangle has three sides. 1, 2, 3!'],
  ['12',  'a dozen! Like a big carton of eggs.'],
  ['0',   'zero means none at all — an empty basket.'],
  ['100', 'ten tens make one hundred. That is a LOT!'],
  ['5',   'one whole hand of fingers. High five!'],
  ['8',   'turn it on its side and it is infinity ∞'],
  ['2',   'a pair — like two shoes or two eyes.'],
  ['20',  'all your fingers AND all your toes!'],
  ['6',   'a bug has six legs. Count them!'],
  ['4',   'a square has four corners. 1, 2, 3, 4!'],
  ['9',   'one less than ten. Almost there!'],
  ['50',  'half of one hundred — five whole tens.'],
  ['1',   'the very first number. You are number one!'],
  ['15',  'a ten and a five make fifteen.'],
];

function numberOfTheDay() {
  const now = new Date();
  const idx = (now.getMonth() * 31 + now.getDate()) % NUMBERS_OF_THE_DAY.length;
  return NUMBERS_OF_THE_DAY[idx];
}

// A toy number-block face: bright background, big friendly digit.
function makeDigitTexture(digit, bg, ink) {
  return makeCanvasTexture(128, 128, (ctx) => {
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, 128, 128);
    ctx.strokeStyle = 'rgba(255,255,255,0.7)';
    ctx.lineWidth = 7;
    ctx.strokeRect(8, 8, 112, 112);
    ctx.fillStyle = ink;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = '900 84px ui-sans-serif, system-ui, sans-serif';
    ctx.fillText(digit, 64, 70);
  });
}

function buildNumberBlock(ctx, digit, bg, x, z, yaw, size = 0.42) {
  const gy = heightAt(x, z);
  const block = new THREE.Mesh(
    new THREE.BoxGeometry(size, size, size),
    new THREE.MeshLambertMaterial({ map: makeDigitTexture(digit, bg, '#ffffff') })
  );
  block.position.set(x, gy + size / 2, z);
  block.rotation.y = yaw;
  block.castShadow = !LOW;
  ctx.propsGroup.add(block);
}

// The giant abacus: a monument-sized counting frame whose bead rows slide
// gently. Chromebook discipline: the frame + all four wires merge into ONE
// static mesh, and each row's beads merge into ONE mesh that slides as a unit —
// so the whole monument is 5 draw calls (1 frame + 4 rows), not ~25 loose meshes.
const _beadRows = [];   // { mesh, phase }
function buildGiantAbacus(ctx, x, z) {
  const group = new THREE.Group();
  const FRAME = PALETTE.woodDark, W = 2.4, H = 2.4;
  const beadColors = [0xe05656, 0x4a8ed0, 0xe0a832, 0x6aa84a];
  const rows = 4, per = LOW ? 4 : 5;

  // Frame (two uprights + top/bottom rails) + the four wires, all one mesh.
  const frameParts = [];
  const box = (w, h, d, px, py) => {
    const g = new THREE.BoxGeometry(w, h, d); g.translate(px, py, 0); frameParts.push(g);
  };
  box(0.22, H, 0.22, -W / 2, H / 2);
  box(0.22, H, 0.22,  W / 2, H / 2);
  box(W + 0.22, 0.22, 0.22, 0, H - 0.11);
  box(W + 0.22, 0.22, 0.22, 0, 0.11);
  const frame = new THREE.Mesh(mergeGeometries(frameParts, false), new THREE.MeshLambertMaterial({ color: FRAME }));
  for (const g of frameParts) g.dispose();
  frame.castShadow = !LOW;
  group.add(frame);
  const wireParts = [];
  for (let r = 0; r < rows; r++) {
    const wy = 0.5 + r * ((H - 1.0) / (rows - 1));
    const g = new THREE.BoxGeometry(W, 0.03, 0.03); g.translate(0, wy, 0); wireParts.push(g);
  }
  const wires = new THREE.Mesh(mergeGeometries(wireParts, false), new THREE.MeshLambertMaterial({ color: 0x8a8375 }));
  for (const g of wireParts) g.dispose();
  group.add(wires);

  // Each row's beads merged into one sliding mesh.
  for (let r = 0; r < rows; r++) {
    const wy = 0.5 + r * ((H - 1.0) / (rows - 1));
    const beadParts = [];
    for (let i = 0; i < per; i++) {
      const g = new THREE.SphereGeometry(0.16, 10, 8);
      g.translate(-W / 2 + 0.28 + i * 0.34, wy, 0);
      beadParts.push(g);
    }
    const rowMesh = new THREE.Mesh(mergeGeometries(beadParts, false),
      new THREE.MeshLambertMaterial({ color: beadColors[r % beadColors.length] }));
    for (const g of beadParts) g.dispose();
    rowMesh.castShadow = !LOW;
    markDynamic(rowMesh);   // updateNumberton slides the bead row
    group.add(rowMesh);
    _beadRows.push({ mesh: rowMesh, phase: r * 0.7 });
  }

  const gy = heightAt(x, z);
  group.position.set(x, gy, z);
  ctx.propsGroup.add(group);
  addObstacle({ minX: x - W / 2, maxX: x + W / 2, minY: gy, maxY: gy + H, minZ: z - 0.3, maxZ: z + 0.3 });

  // Bump the abacus and it teaches today's number.
  const [num, def] = numberOfTheDay();
  ctx.addBumpZone(x, z + 0.7, 1.9, 'abacus', (sourceId) => {
    ctx.showBumpMsg(`🔢 Number of the Day: ${num}`, def, 7, sourceId);
  });
}

// Called from the main loop while in play mode — slides the abacus bead rows.
export function updateNumberton(dt, t, playerPos) {
  const dx = playerPos.x - NUMBERTON_CENTER.x, dz = playerPos.z - NUMBERTON_CENTER.z;
  if (dx * dx + dz * dz > 2500) return;            // dozes past ~50 units
  for (let r = 0; r < _beadRows.length; r++) {
    const row = _beadRows[r];
    row.mesh.position.x = Math.sin(t * 0.6 + row.phase) * 0.16;   // lazy counting slide
  }
}

export function buildNumberton(ctx) {
  const { z: CZ } = NUMBERTON_CENTER;

  // ---- The road in: continue the south road from the Village Green out to the
  // plaza, then a spine with spurs to each doorway (mirror of the other zones).
  ctx.buildPath([[0, -28], [0, -31], [0, -34], [0, -38]]);
  ctx.buildPath([[0, -38], [0, -41], [0, -44], [0, -47.5]]);
  ctx.buildPath([[0, -41], [-2.4, -41.5]]);        // Tally Time
  ctx.buildPath([[0, -41], [2.4, -41.5]]);         // Add It Up
  ctx.buildPath([[0, -47], [-2.4, -47.5]]);        // Shape Up
  ctx.buildPath([[0, -47], [2.4, -47.5]]);         // Counting House
  ctx.buildPath([[0, -44], [0, -50]]);             // up to the Abacus

  // Daisies along the walk in — little colour beats on the journey.
  const numFlowers = [0xe05656, 0x4a8ed0, 0xe0a832, 0x6aa84a];
  ctx.buildFlowerPatch(1.7, -30.4, 5, 0.55, numFlowers);
  ctx.buildFlowerPatch(-1.8, -33.8, 5, 0.55, numFlowers);

  // ---- Welcome sign at the zone entrance (faces the incoming player from the
  // north), and the signpost home.
  // Signs face +z (north), toward the player arriving from the village — the
  // mirror of Obby Peak's Math.PI-facing signs to the north.
  ctx.addHutSign(1.7, -37.5, 'NUMBERTON', { x: 1.7, y: 0, z: -37.5, yaw: 0 });
  ctx.buildSignpost(-1.5, -37.0, [{ text: 'CHOCKYTOWN', yaw: 0, flip: true }]);
  ctx.addBumpZone(1.7, -37.5, 1.3, 'numberton-sign', (sourceId) => {
    ctx.showBumpMsg('WELCOME TO NUMBERTON!', 'Where numbers come out to count 🔢', 4, sourceId);
  });

  // ---- Game huts (doors face +z, toward the arriving player). Math theme; all
  // Coming Soon until their slugs join PLAYABLE_GAME_SLUGS in main.js. Rooftop
  // signs angle toward the diorama camera; nudge them with the ! editor.
  //                    x     z     w    d    wallH wall      roof      smoke  name          slug         rooftop sign
  ctx.buildGameHut(-5.5, -42, 2.4, 2.2, 1.5, BUILDING_SCHEMES.numberZoo.wall, BUILDING_SCHEMES.numberZoo.roof, true,  'TALLY TIME', 'tally-time', { x: -5.5, y: 1.30, z: -42, yaw: 0 });
  ctx.buildGameHut( 5.5, -42, 2.2, 2.4, 1.4, BUILDING_SCHEMES.mathChef.wall, BUILDING_SCHEMES.mathChef.roof, false, 'ADD IT UP', 'add-it-up', { x: 5.5, y: 1.20, z: -42, yaw: 0 });
  ctx.buildGameHut(-5.5, -48, 2.4, 2.0, 1.6, BUILDING_SCHEMES.adventureBlue.wall, BUILDING_SCHEMES.adventureBlue.roof, true,  'SHAPE UP', 'shape-up', { x: -5.5, y: 1.40, z: -48, yaw: 0 });

  // ---- The Counting House: the zone's hero building. Handmade kit model when
  // it loaded; the procedural big house otherwise.
  if (!ctx.buildKitStructure || !ctx.buildKitStructure('house_big', 5.5, -48.5, { name: 'Counting House' })) {
    ctx.buildHouse(5.5, -48.5, 4.6, 3.2, 2.5, PALETTE.sand, PALETTE.roofBrown, false);
  }
  ctx.addHutSign(5.5, -48.5, 'COUNTING HOUSE', { x: 5.5, y: 2.35, z: -48.5, yaw: 0 });
  ctx.addBumpZone(5.5, -46.7, 1.6, 'counting-house', (sourceId) => {
    ctx.showBumpMsg('THE COUNTING HOUSE 🧮', 'Adding up sums… story problems coming soon!', 4, sourceId);
  });

  // ---- The giant abacus, looming kindly over the plaza's south side.
  buildGiantAbacus(ctx, 0, CZ - 8);
  // A little counting garden of number blocks at its feet: 1, 2, 3.
  buildNumberBlock(ctx, '1', '#e05656', -1.6, CZ - 6.4, 0.4);
  buildNumberBlock(ctx, '2', '#4a8ed0', 0.0, CZ - 6.6, -0.3);
  buildNumberBlock(ctx, '3', '#e0a832', 1.6, CZ - 6.2, 0.9, 0.34);

  // ---- Dressing: lanterns, flowers, trees, bushes, mushrooms.
  ctx.buildLantern(-2, CZ + 2.4, false);
  ctx.buildLantern( 2, CZ - 2.4, false);
  ctx.buildFlowerPatch(-2.6, CZ + 1.6, 7, 0.8, numFlowers);
  ctx.buildFlowerPatch( 2.6, CZ - 1.7, 7, 0.8, numFlowers);
  ctx.buildFlowerPatch( 0.6, CZ + 5.3, 6, 0.7, numFlowers);
  ctx.buildTree(-8.2, CZ - 5.0, 1.1);
  ctx.buildTree( 8.0, CZ - 6.0, 1.2);
  ctx.buildTree(-7.5, CZ + 3.2, 0.95);
  ctx.buildTree( 7.6, CZ + 3.6, 1.0);
  ctx.buildBush(-6.6, CZ + 5.9, 0.9);
  ctx.buildBush( 6.8, CZ + 1.4, 0.8);
  ctx.buildRock(-8.4, CZ + 1.9, 0.9);
  ctx.buildRock( 8.6, CZ - 1.5, 0.8);
  ctx.buildMushroomCluster(-6.4, CZ - 6.6, 3);
  ctx.buildMushroomCluster( 7.4, CZ + 6.0, 2);
}
